import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Link2, Loader2, Search, Unlink } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";

const DesignPicker = ({ open, setOpen, onPick }) => {
  const [q, setQ] = useState("");
  const [items, setItems] = useState(null);
  const [url, setUrl] = useState("");
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => api.get("/canva/designs", { params: { q } }).then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e))), 300);
    return () => clearTimeout(t);
  }, [q, open]);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-[var(--cream)] max-w-xl">
        <DialogHeader><DialogTitle>Link a Canva design</DialogTitle></DialogHeader>
        <p className="text-xs text-[var(--muted-ink)]">New product pages will be inserted into this design, just before its last (contact) page.</p>
        <div className="relative"><Search size={14} className="absolute left-3 top-3 text-[var(--muted-ink)]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search your Canva designs…" className="pl-9" data-testid="canva-design-search" /></div>
        <div className="max-h-72 overflow-y-auto border border-[var(--line)]" data-testid="canva-design-list">
          {items === null ? <p className="p-4 text-sm"><Loader2 size={14} className="inline animate-spin mr-2" />Loading designs…</p>
            : items.length === 0 ? <p className="p-4 text-sm text-[var(--muted-ink)]">No designs found</p>
            : items.map((d) => (
              <button key={d.design_id} onClick={() => onPick(d.design_id)} data-testid={`canva-design-${d.design_id}`}
                className="w-full flex items-center gap-3 p-3 border-b border-[var(--line)] text-left hover:bg-[var(--paper)] transition-colors">
                {d.thumbnail_url ? <img src={d.thumbnail_url} alt="" className="w-20 aspect-[16/9] object-cover bg-[var(--line)]" /> : <div className="w-20 aspect-[16/9] bg-[var(--line)]" />}
                <span className="flex-1 text-sm font-medium">{d.title}</span>
                <span className="text-xs text-[var(--muted-ink)]">{d.page_count} pages</span>
              </button>
            ))}
        </div>
        <div className="flex gap-2">
          <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="…or paste a Canva design link" data-testid="canva-design-url-input" />
          <Button variant="outline" disabled={!url} onClick={() => onPick(url)} data-testid="canva-design-url-link">Link</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const CanvaMasterCard = ({ cat, onChange }) => {
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/canva/status").then((r) => setStatus(r.data)); }, []);
  const m = cat.canva_master || {};

  const link = async (value) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/canva/link/category/${cat.id}`, { url: value });
      toast.success(`Linked to “${data.title}”`); setOpen(false); onChange();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const unlink = async () => { await api.delete(`/canva/link/category/${cat.id}`); toast.success("Canva design unlinked"); onChange(); };

  if (!status) return null;
  return (
    <div className="flex flex-wrap items-center gap-4 bg-[var(--cream)] border border-[var(--line)] px-5 py-4 mb-10" data-testid="canva-master-card">
      <p className="text-[11px] tracking-[0.25em] uppercase text-[var(--muted-ink)]">Canva master</p>
      {!status.connected ? (
        <p className="text-sm">Not connected · <Link to="/settings" className="underline">connect Canva in Settings</Link></p>
      ) : m.design_id ? (
        <>
          <p className="text-sm font-semibold" data-testid="canva-master-title">{m.title}</p>
          <p className="text-xs text-[var(--muted-ink)]" data-testid="canva-master-pages">{m.page_count} pages · new products go in before the last page</p>
          <div className="flex gap-2 ml-auto">
            <Button asChild size="sm" variant="outline" data-testid="canva-master-open"><a href={m.edit_url} target="_blank" rel="noreferrer"><ExternalLink size={13} className="mr-1" />Open in Canva</a></Button>
            <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="canva-master-change">Change</Button>
            <Button size="sm" variant="outline" onClick={unlink} data-testid="canva-master-unlink"><Unlink size={13} /></Button>
          </div>
        </>
      ) : (
        <>
          <p className="text-sm text-[var(--muted-ink)]">Link your existing Canva catalog so new product pages are added straight into it.</p>
          <Button size="sm" className="ml-auto" disabled={busy} onClick={() => setOpen(true)} data-testid="canva-master-link-button">
            {busy ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Link2 size={13} className="mr-1" />}Link Canva design</Button>
        </>
      )}
      <DesignPicker open={open} setOpen={setOpen} onPick={link} />
    </div>
  );
};
